const POSTER_HEADLINE = 'Scan to sign before you hunt'

export type QrPosterPrintInput = {
  orgName: string
  orgLogoUrl?: string
  qrImageDataUrl: string
  documentNames: string[]
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function buildQrPosterHtml(input: QrPosterPrintInput): string {
  const { orgName, orgLogoUrl, qrImageDataUrl, documentNames } = input

  const logoBlock = orgLogoUrl
    ? `<img class="logo" src="${escapeHtml(orgLogoUrl)}" alt="${escapeHtml(orgName)}" />`
    : ''

  const documentsBlock = documentNames.length
    ? `<section class="documents">
    <h3>${documentNames.length === 1 ? 'Document' : 'Documents'} you'll complete</h3>
    <ul>${documentNames.map((name) => `<li>${escapeHtml(name)}</li>`).join('')}</ul>
  </section>`
    : ''

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(orgName)} — ${POSTER_HEADLINE}</title>
<style>
  @page { size: letter; margin: 0; }
  * { box-sizing: border-box; }
  html, body { height: 100%; }
  body {
    margin: 0;
    font-family: sans-serif;
    color: #111;
    text-align: center;
  }
  .poster {
    display: flex;
    height: 100vh;
    padding: 0.6in 0.75in;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    page-break-inside: avoid;
    page-break-after: avoid;
  }
  .logo {
    max-height: 72px;
    max-width: 260px;
    object-fit: contain;
    margin-bottom: 20px;
  }
  h1 {
    margin: 0 0 4px;
    font-size: 30px;
  }
  h2 {
    margin: 0 0 28px;
    font-size: 20px;
    font-weight: 500;
    color: #444;
  }
  .qr {
    width: 5.25in;
    height: 5.25in;
  }
  .documents {
    margin-top: 28px;
    width: 5.25in;
    border: 2px solid #111;
    border-radius: 12px;
    padding: 16px 24px;
    text-align: left;
  }
  .compact .qr {
    width: 4.25in;
    height: 4.25in;
  }
  .documents h3 {
    margin: 0 0 10px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #444;
  }
  .documents ul {
    list-style: disc;
    margin: 0;
    padding-left: 22px;
    font-size: 20px;
    color: #111;
  }
  .documents li {
    margin-bottom: 6px;
  }
</style>
</head>
<body>
  <div class="poster${documentNames.length > 3 ? ' compact' : ''}">
    ${logoBlock}
    <h1>${escapeHtml(orgName)}</h1>
    <h2>${POSTER_HEADLINE}</h2>
    <img class="qr" src="${qrImageDataUrl}" alt="QR code" />
    ${documentsBlock}
  </div>
</body>
</html>`
}

export function printQrPoster(input: QrPosterPrintInput): void {
  const printWindow = window.open('', '_blank', 'width=850,height=1100')
  if (!printWindow) return
  printWindow.document.write(buildQrPosterHtml(input))
  printWindow.document.close()
  void waitForImages(printWindow.document).then(() => {
    printWindow.focus()
    printWindow.print()
  })
}

function waitForImages(doc: Document): Promise<void> {
  const pending = Array.from(doc.images).map((image) =>
    image.complete && image.naturalWidth > 0
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
          image.addEventListener('load', () => resolve(), { once: true })
          image.addEventListener('error', () => resolve(), { once: true })
        }),
  )
  return Promise.all(pending).then(() => undefined)
}
